import React, { useCallback, useEffect, useState } from 'react';
import { BrainCircuit, RefreshCw, Mic, ScanText, Sparkles, CheckCircle2, AlertTriangle, AlertOctagon, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import baseURL from '../../utils/baseURL';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import AiUsageLog from './AiUsageLog';
import { formatTime } from '../../utils/clock';

// Refresh cadence for the live status cards. Fast enough to catch an outage
// quickly, slow enough not to hammer Redis + Mitra One Care AI on every tick.
const POLL_MS = 20000;

const CARD_ICONS = {
    transcription: Mic,
    ocr: ScanText,
    assistant: Sparkles,
};

// healthy > good > medium > low > disconnected — five priority tiers so a
// glance at color tells you how urgently to look.
const PRIORITY = {
    healthy: { label: 'Healthy', dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    good: { label: 'Good', dot: 'bg-teal-500', badge: 'bg-teal-100 text-teal-700', icon: CheckCircle2 },
    medium: { label: 'Medium', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
    low: { label: 'Low', dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-700', icon: AlertOctagon },
    disconnected: { label: 'Disconnected', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700', icon: XCircle },
};
const priorityMeta = (p) => PRIORITY[p] || PRIORITY.disconnected;

function AiFeatureCard({ card }) {
    const Icon = CARD_ICONS[card.key] || BrainCircuit;
    const meta = priorityMeta(card.priority);
    const StatusIcon = meta.icon;
    return (
        <div
            data-testid={`ai-log-card-${card.key}`}
            className="bg-white rounded-2xl shadow-md p-4 sm:p-5 flex flex-col gap-3 border border-gray-100"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#4ABDBD]/10 text-[#2D8A8A] shrink-0">
                        <Icon className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-800">{card.title}</div>
                        <div className="text-[11px] text-slate-400">{card.description}</div>
                    </div>
                </div>
            </div>

            <div className={`inline-flex items-center gap-1.5 self-start px-2.5 py-1 rounded-full text-xs font-semibold ${meta.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                <StatusIcon className="w-3.5 h-3.5" />
                {meta.label}
            </div>

            <p className="text-xs text-slate-500 leading-relaxed">{card.detail}</p>
        </div>
    );
}

// Super Admin > Audit & Security > AI Log. Live connectivity/priority status
// for the three AI feature pipelines (transcription, OCR, assistant &
// summaries), pulled from GET /audit/ai-status and auto-refreshed — and,
// below them, the usage log: one row per AI use (AiUsageLog, owner 2 Oct 2026).
export default function AiLog() {
    const { t } = useTranslation();
    const axiosInstance = useAxiosAuth();
    // Bumped by the Refresh button so the usage table reloads with the cards
    // (the 20 s poll refreshes the cards only — the table keeps its page).
    const [refreshKey, setRefreshKey] = useState(0);
    const [cards, setCards] = useState(null);
    const [checkedAt, setCheckedAt] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    const load = useCallback(async (isFirst) => {
        if (isFirst) setLoading(true); else setRefreshing(true);
        try {
            const res = await axiosInstance.get(`${baseURL}/audit/ai-status`);
            const data = res.data?.data || res.data;
            setCards(data?.cards || []);
            setCheckedAt(data?.checkedAt || null);
        } catch {
            if (isFirst) toast.warn(t('aiLog.status_failed'));
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        let alive = true;
        load(true);
        const timer = setInterval(() => { if (alive) load(false); }, POLL_MS);
        return () => { alive = false; clearInterval(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-full w-full" data-testid="audit-page-ai">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-2">
                        <BrainCircuit className="w-6 h-6 text-[#4ABDBD]" /> {t('aiLog.title')}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {t('aiLog.subtitle')}
                    </p>
                </div>
                <button
                    type="button"
                    data-testid="ai-log-refresh-button"
                    onClick={() => { load(false); setRefreshKey((k) => k + 1); }}
                    disabled={refreshing || loading}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white border border-gray-200 text-sm font-medium text-slate-600 shadow-sm hover:shadow-md hover:border-[#4ABDBD]/40 transition-all duration-200 disabled:opacity-50 cursor-pointer"
                >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                    {t('aiLog.refresh')}
                </button>
            </div>

            {checkedAt && (
                <p className="text-[11px] text-slate-400 mt-2">{t('aiLog.last_checked', { time: formatTime(checkedAt) })}</p>
            )}

            {loading ? (
                <div className="bg-white rounded-2xl shadow-md p-10 mt-4 flex items-center justify-center">
                    <Loader2 className="animate-spin text-[#4ABDBD]" size={22} />
                </div>
            ) : !cards || cards.length === 0 ? (
                <div className="bg-white rounded-2xl shadow-md p-10 mt-4 text-center text-sm text-slate-400">
                    {t('aiLog.no_status')}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                    {cards.map((card) => (
                        <AiFeatureCard key={card.key} card={card} />
                    ))}
                </div>
            )}

            <AiUsageLog refreshKey={refreshKey} />
        </div>
    );
}
